import React, { useState } from 'react';
import Select, { MultiValue } from 'react-select';
import { X } from 'lucide-react';
import { ListBuilderItem } from '../ListBuilder';
import LgmButton from '../../../LgmButton';
import {
  GRAY_100,
  GRAY_200,
  GRAY_300,
  GRAY_400,
  GRAY_50,
  GRAY_500,
  GRAY_700,
  GRAY_900,
} from 'theme/grayScale';
import { COLOR_INFO } from 'theme/colors';

interface AddItemModalProps {
  title: string;
  availableItems: ListBuilderItem[];
  onConfirm: (selectedIds: number[]) => void;
  onCancel: () => void;
}

const AddItemModal: React.FC<AddItemModalProps> = ({
  title,
  availableItems,
  onConfirm,
  onCancel,
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const selectOptions = availableItems.map(item => ({
    value: item.id.toString(),
    label: item.label,
  }));

  const handleChange = (
    selected: MultiValue<{ value: string; label: string }>
  ) => {
    const ids = selected.map(opt => parseInt(opt.value, 10));
    setSelectedIds(ids);
  };

  const handleConfirm = () => {
    if (selectedIds.length > 0) {
      onConfirm(selectedIds);
    }
  };

  return (
    <>
      {/* Modal Overlay */}
      <div style={modalStyles.overlay} onClick={onCancel} />

      {/* Modal Content */}
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <h3 style={modalStyles.title}>{title}</h3>
          <LgmButton
            variant="flat"
            onClick={onCancel}
            style={modalStyles.closeButton}
            hoverStyle={modalStyles.closeButtonHover}
            pressedStyle={modalStyles.closeButtonPressed}
            aria-label="Close">
            <X size={20} />
          </LgmButton>
        </div>

        <div style={modalStyles.body}>
          <div style={modalStyles.fieldContainer}>
            <label style={modalStyles.label}>Select items to add</label>
            <Select
              value={selectOptions.filter(opt =>
                selectedIds.includes(parseInt(opt.value, 10))
              )}
              onChange={handleChange}
              options={selectOptions}
              isMulti
              placeholder="Select..."
              menuPortalTarget={document.body}
              styles={{
                control: base => ({
                  ...base,
                  minHeight: '40px',
                  borderColor: GRAY_300,
                  '&:hover': {
                    borderColor: GRAY_400,
                  },
                }),
                menuPortal: base => ({ ...base, zIndex: 10002 }),
              }}
            />
          </div>
        </div>

        <div style={modalStyles.footer}>
          <LgmButton
            variant="outlined"
            onClick={onCancel}
            style={modalStyles.cancelButton}
            hoverStyle={{
              backgroundColor: GRAY_50,
              border: `1px solid ${GRAY_400}`,
            }}
            pressedStyle={{
              backgroundColor: GRAY_100,
              border: `1px solid ${GRAY_400}`,
            }}>
            Cancel
          </LgmButton>
          <LgmButton
            variant="contained"
            onClick={handleConfirm}
            disabled={selectedIds.length === 0}
            style={modalStyles.confirmButton}>
            Add Selected ({selectedIds.length})
          </LgmButton>
        </div>
      </div>
    </>
  );
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 10001,
  },
  modal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
    zIndex: 10002,
    width: '90%',
    maxWidth: '500px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${GRAY_200}`,
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: GRAY_900,
    margin: 0,
  },
  closeButton: {
    padding: '4px',
    backgroundColor: 'transparent',
    border: 'none',
    color: GRAY_500,
    cursor: 'pointer',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  closeButtonHover: {
    color: GRAY_700,
    backgroundColor: GRAY_100,
    padding: '4px',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  closeButtonPressed: {
    color: GRAY_700,
    backgroundColor: GRAY_200, // active: one step darker than hover
    padding: '4px',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  body: {
    padding: '24px',
    flex: 1,
    overflowY: 'auto',
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: GRAY_700,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: `1px solid ${GRAY_200}`,
  },
  cancelButton: {
    padding: '8px 16px',
    backgroundColor: '#ffffff',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '6px',
    color: GRAY_700,
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  confirmButton: {
    padding: '8px 16px',
    backgroundColor: COLOR_INFO,
    border: 'none',
    borderRadius: '6px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

export default React.memo(AddItemModal);
